import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { create } from '@bufbuild/protobuf';
import {
  App,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Space,
  Spin,
  Steps,
  Typography,
} from 'antd';
import { ScanLine } from 'lucide-react';
import { QRCode } from 'react-qr-code';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { channelApi } from '@/api/channel.api';
import { applicationApi } from '@/api/application.api';
import { agentApi } from '@/api/agent.api';
import { ApiError } from '@/api/errors';
import {
  ChannelAccountBindingMode,
  ChatPolicySchema,
  ChannelType,
  type ChannelAccount,
} from '@/proto/agentplatform/v1/channel_pb';
import {
  StartFeishuRegisterRequestSchema,
  FeishuRegisterState,
  type FeishuRegisterStatus,
} from '@/proto/agentplatform/httpv2/harness_pb';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { channelTypeConventionallySupportsGroup, parseIdList } from './helpers';

const APPLICATION_PICKER_PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 2000;

interface FeishuRegisterModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type RegisterStep = 'config' | 'scanning' | 'result';

export default function FeishuRegisterModal({
  open,
  onClose,
  onSuccess,
}: FeishuRegisterModalProps) {
  const { message } = App.useApp();

  // — Step control —
  const [step, setStep] = useState<RegisterStep>('config');

  // — Config form state —
  const [formName, setFormName] = useState('');
  const [formBindingMode, setFormBindingMode] = useState<ChannelAccountBindingMode>(
    ChannelAccountBindingMode.INSTANCE
  );
  const [formApplicationId, setFormApplicationId] = useState('');
  const [formP2pAllowFrom, setFormP2pAllowFrom] = useState('');
  const [formDisableGroup, setFormDisableGroup] = useState(false);
  const [formGroupAllowFrom, setFormGroupAllowFrom] = useState('');
  const [formShowToolCalls, setFormShowToolCalls] = useState(true);
  const [formApprovers, setFormApprovers] = useState('');
  /** 可自我审批人员，命中后其发起的审批可自行通过（chat_policy.self_approvers）。 */
  const [formSelfApprovers, setFormSelfApprovers] = useState('');
  const [formUnknownCommandAsAgentInput, setFormUnknownCommandAsAgentInput] = useState(false);

  // — Scanning state —
  const [, setRegistrationId] = useState('');
  const [registerStatus, setRegisterStatus] = useState<FeishuRegisterStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // — Result state —
  const [createdAccount, setCreatedAccount] = useState<ChannelAccount | null>(null);
  const [registerError, setRegisterError] = useState('');

  const channelSupportsGroup = channelTypeConventionallySupportsGroup(ChannelType.FEISHU);

  const resetForm = useCallback(() => {
    setFormName('');
    setFormBindingMode(ChannelAccountBindingMode.INSTANCE);
    setFormApplicationId('');
    setFormP2pAllowFrom('');
    setFormDisableGroup(false);
    setFormGroupAllowFrom('');
    setFormShowToolCalls(true);
    setFormApprovers('');
    setFormSelfApprovers('');
    setFormUnknownCommandAsAgentInput(false);
  }, []);

  const resetAll = useCallback(() => {
    resetForm();
    setStep('config');
    setRegistrationId('');
    setRegisterStatus(null);
    setPolling(false);
    setCreatedAccount(null);
    setRegisterError('');
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [resetForm]);

  // — Applications infinite query —
  const {
    data: applicationsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: applicationsLoading,
  } = useInfiniteQuery({
    queryKey: ['applications', 'feishu_register_infinite'] as const,
    initialPageParam: 1,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      applicationApi.list(
        create(ListRequestSchema, {
          page: pageParam,
          pageSize: APPLICATION_PICKER_PAGE_SIZE,
        })
      ),
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.total;
      const loaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      if (loaded >= total) return undefined;
      return allPages.length + 1;
    },
    enabled: open,
  });

  const applications = useMemo(
    () => applicationsPages?.pages.flatMap((p) => p.data).filter(Boolean) ?? [],
    [applicationsPages?.pages]
  );

  const { data: agentsResponse } = useQuery({
    queryKey: ['agents', 'list', 'for-feishu-register'] as const,
    queryFn: () => agentApi.listAgents(create(ListRequestSchema, { page: 1, pageSize: 200 })),
    enabled: open && formBindingMode === ChannelAccountBindingMode.TEMPLATE,
  });
  const agents = useMemo(() => agentsResponse?.data?.filter(Boolean) ?? [], [agentsResponse?.data]);

  const applicationPickerScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 56;
    if (nearBottom && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  // — Polling logic —
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setPolling(false);
  }, []);

  const startPolling = useCallback(
    (regId: string) => {
      stopPolling();
      setPolling(true);
      pollTimerRef.current = setInterval(async () => {
        try {
          const status = await channelApi.getFeishuRegisterStatus(regId);
          setRegisterStatus(status);
          if (
            status.status === FeishuRegisterState.COMPLETED ||
            status.status === FeishuRegisterState.FAILED
          ) {
            stopPolling();
            if (status.status === FeishuRegisterState.COMPLETED && status.channelAccount) {
              setCreatedAccount(status.channelAccount);
              setStep('result');
            } else if (status.status === FeishuRegisterState.FAILED) {
              setRegisterError(status.error || '注册失败');
              setStep('result');
            }
          }
        } catch {
          // Network error — keep polling
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling]
  );

  // Cleanup on unmount / close
  useEffect(() => {
    if (!open) {
      stopPolling();
    }
    return () => stopPolling();
  }, [open, stopPolling]);

  // — Start registration (always create mode, Feishu side provides choice) —
  const handleStartRegister = async () => {
    if (!formName.trim()) {
      message.error('请输入账号名称');
      return;
    }
    if (!formApplicationId) {
      message.error('请选择绑定的 Agent');
      return;
    }

    const chatPolicy = create(ChatPolicySchema, {
      p2pAllowFrom: parseIdList(formP2pAllowFrom),
      disableGroup: channelSupportsGroup ? formDisableGroup : false,
      groupAllowFrom: channelSupportsGroup ? parseIdList(formGroupAllowFrom) : [],
      showToolCalls: channelSupportsGroup ? formShowToolCalls : true,
      approvers: channelSupportsGroup ? parseIdList(formApprovers) : [],
      selfApprovers: channelSupportsGroup ? parseIdList(formSelfApprovers) : [],
      unknownCommandAsAgentInput: formUnknownCommandAsAgentInput,
    });

    const req = create(StartFeishuRegisterRequestSchema, {
      name: formName.trim(),
      agentId: formApplicationId,
      bindingMode: formBindingMode,
      chatPolicy,
    });

    try {
      const status = await channelApi.startFeishuRegister(req);
      setRegistrationId(status.registrationId);
      setRegisterStatus(status);
      setStep('scanning');
      startPolling(status.registrationId);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  // — Retry —
  const handleRetry = () => {
    setRegisterStatus(null);
    setCreatedAccount(null);
    setRegisterError('');
    setStep('config');
  };

  // — Close & success —
  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleSuccessClose = () => {
    resetAll();
    onSuccess();
  };

  // — Step index —
  const stepIndex = step === 'config' ? 0 : step === 'scanning' ? 1 : 2;

  // — Scanning step status text —
  const scanStatusText = (() => {
    if (!registerStatus) return '正在生成二维码…';
    switch (registerStatus.status) {
      case FeishuRegisterState.SCANNING:
        return '请使用飞书管理员账号扫描下方二维码，可选择创建新应用或选择已有应用';
      case FeishuRegisterState.CREATING:
        return '已授权，正在配置飞书应用…';
      case FeishuRegisterState.COMPLETED:
        return '配置成功！';
      case FeishuRegisterState.FAILED:
        return '操作失败';
      default:
        return '等待中…';
    }
  })();

  const qrCodeUrl = registerStatus?.qrCodeUrl;

  // — Config form validation —
  const configValid = formName.trim().length > 0 && formApplicationId.length > 0;

  const pickerBoxStyle: React.CSSProperties = {
    maxHeight: 192,
    overflowY: 'auto',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-sm)',
  };
  const pickerItemStyle = (selected: boolean): React.CSSProperties => ({
    padding: '10px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--border-color-lighter)',
    background: selected ? 'var(--color-primary-light-9)' : undefined,
    fontWeight: selected ? 'var(--fw-medium)' : undefined,
  });
  const pickerHintStyle: React.CSSProperties = {
    padding: 12,
    color: 'var(--text-color-placeholder)',
  };

  return (
    <Modal
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ScanLine size={18} />
          一键扫码配置飞书机器人
        </span>
      }
      open={open}
      onCancel={step === 'result' && createdAccount ? handleSuccessClose : handleClose}
      width={560}
      destroyOnClose
      footer={null}
    >
      <Steps
        current={stepIndex}
        size="small"
        style={{ marginBottom: 24 }}
        items={[{ title: '配置' }, { title: '扫码' }, { title: '完成' }]}
      />

      {/* Step 1: Config */}
      {step === 'config' && (
        <Form layout="vertical">
          <Form.Item label="账号名称" required>
            <Input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="飞书机器人显示名称"
            />
          </Form.Item>

          <Form.Item label="绑定模式">
            <Select<ChannelAccountBindingMode>
              value={formBindingMode}
              onChange={(m) => {
                setFormBindingMode(m);
                setFormApplicationId('');
              }}
              options={[
                {
                  value: ChannelAccountBindingMode.INSTANCE,
                  label: '实例绑定（agent_id 指向应用实例）',
                },
                {
                  value: ChannelAccountBindingMode.TEMPLATE,
                  label: '模板绑定（agent_id 指向 Agent 模版，用户需 /init）',
                },
              ]}
            />
          </Form.Item>

          {formBindingMode === ChannelAccountBindingMode.INSTANCE ? (
            <Form.Item
              label="智能体实例"
              required
              extra="点击选择实例；列表滚动到底自动加载下一页。"
            >
              <div
                role="listbox"
                aria-label="智能体实例"
                style={pickerBoxStyle}
                onScroll={applicationPickerScroll}
              >
                {applicationsLoading && applications.length === 0 ? (
                  <div style={pickerHintStyle}>加载中…</div>
                ) : applications.length === 0 ? (
                  <div style={pickerHintStyle}>暂无应用实例，请先在「应用」中创建。</div>
                ) : (
                  <>
                    {applications.map((app) => {
                      const label = app.name?.trim() ? `${app.name} (${app.id})` : app.id;
                      const selected = formApplicationId === app.id;
                      return (
                        <div
                          key={app.id}
                          role="option"
                          aria-selected={selected}
                          title={app.id}
                          onClick={() => setFormApplicationId(app.id)}
                          style={pickerItemStyle(selected)}
                        >
                          {label}
                        </div>
                      );
                    })}
                    {isFetchingNextPage && (
                      <div style={{ ...pickerHintStyle, textAlign: 'center', fontSize: 12 }}>
                        加载更多…
                      </div>
                    )}
                  </>
                )}
              </div>
            </Form.Item>
          ) : (
            <Form.Item
              label="Agent 模版"
              required
              extra="选择 Agent 模版，终端用户需执行 /init 创建专属实例。"
            >
              <div role="listbox" aria-label="Agent 模版" style={pickerBoxStyle}>
                {agents.length === 0 ? (
                  <div style={pickerHintStyle}>暂无 Agent 模版，请先在「智能体」中创建。</div>
                ) : (
                  agents.map((ag) => {
                    const label = ag.name?.trim() ? `${ag.name} (${ag.id})` : ag.id;
                    const selected = formApplicationId === ag.id;
                    return (
                      <div
                        key={ag.id}
                        role="option"
                        aria-selected={selected}
                        title={ag.id}
                        onClick={() => setFormApplicationId(ag.id)}
                        style={pickerItemStyle(selected)}
                      >
                        {label}
                      </div>
                    );
                  })
                )}
              </div>
            </Form.Item>
          )}

          {/* Chat Policy */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: 12,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-color-lighter)',
              background: 'var(--fill-color-lighter)',
            }}
          >
            <Typography.Text strong>聊天策略</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6 }}>
              单聊/群聊允许列表为空时不限制来源；填写后仅匹配列表中的发送者用户 ID 或群 ID。
            </Typography.Text>

            <Form.Item label="单聊允许的发送者 ID" style={{ marginBottom: 0 }}>
              <Input.TextArea
                value={formP2pAllowFrom}
                onChange={(e) => setFormP2pAllowFrom(e.target.value)}
                placeholder="每行一个用户 ID，或用逗号分隔"
                rows={3}
              />
            </Form.Item>

            {channelSupportsGroup && (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Checkbox
                  checked={formDisableGroup}
                  onChange={(e) => setFormDisableGroup(e.target.checked)}
                >
                  禁用群聊
                </Checkbox>
                <Form.Item label="群聊允许的群 ID" style={{ marginBottom: 0 }}>
                  <Input.TextArea
                    value={formGroupAllowFrom}
                    onChange={(e) => setFormGroupAllowFrom(e.target.value)}
                    placeholder="未勾选「禁用群聊」时生效；空表示允许任意群"
                    rows={3}
                  />
                </Form.Item>
                <Checkbox
                  checked={formShowToolCalls}
                  onChange={(e) => setFormShowToolCalls(e.target.checked)}
                >
                  展示工具调用详情
                </Checkbox>
                <Form.Item label="审批人员 ID" style={{ marginBottom: 0 }}>
                  <Input.TextArea
                    value={formApprovers}
                    onChange={(e) => setFormApprovers(e.target.value)}
                    placeholder="每行一个审批人员 ID，或用逗号分隔"
                    rows={3}
                  />
                </Form.Item>
                <Form.Item label="可自我审批人员 ID" style={{ marginBottom: 0 }}>
                  <Input.TextArea
                    value={formSelfApprovers}
                    onChange={(e) => setFormSelfApprovers(e.target.value)}
                    placeholder="每行一个可自我审批人员 ID，或用逗号分隔"
                    rows={3}
                  />
                </Form.Item>
              </Space>
            )}

            <div>
              <Checkbox
                checked={formUnknownCommandAsAgentInput}
                onChange={(e) => setFormUnknownCommandAsAgentInput(e.target.checked)}
              >
                未命中的斜杠指令透传给 Agent
              </Checkbox>
              <Typography.Text
                type="secondary"
                style={{ display: 'block', fontSize: 12, marginLeft: 24, lineHeight: 1.6 }}
              >
                开启后，未命中系统斜杠指令的消息将作为 Agent 输入运行；关闭则走 /help 兜底（默认）
              </Typography.Text>
            </div>
          </div>

          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, lineHeight: 1.6, marginTop: 12, display: 'block' }}
          >
            扫码时飞书端将展示「创建新应用」或「选择已有应用」的选项，飞书用户可自主选择。
          </Typography.Text>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Button onClick={handleClose}>取消</Button>
            <Button type="primary" disabled={!configValid} onClick={handleStartRegister}>
              生成二维码
            </Button>
          </div>
        </Form>
      )}

      {/* Step 2: Scanning */}
      {step === 'scanning' && (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Typography.Text
            type={
              registerStatus?.status === FeishuRegisterState.CREATING
                ? 'warning'
                : registerStatus?.status === FeishuRegisterState.FAILED
                  ? 'danger'
                  : undefined
            }
            style={{ display: 'block', marginBottom: 20, fontSize: 14 }}
          >
            {scanStatusText}
          </Typography.Text>

          {qrCodeUrl && registerStatus?.status === FeishuRegisterState.SCANNING && (
            <div
              style={{
                display: 'inline-block',
                padding: 16,
                background: '#fff',
                borderRadius: 8,
                border: '1px solid var(--border-color)',
              }}
            >
              <QRCode value={qrCodeUrl} size={200} level="M" />
            </div>
          )}

          {registerStatus?.status === FeishuRegisterState.CREATING && (
            <div style={{ padding: '32px 0' }}>
              <Spin size="large" />
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
                飞书已授权，正在自动配置应用凭证…
              </Typography.Text>
            </div>
          )}

          {registerStatus?.status === FeishuRegisterState.FAILED && (
            <Result
              status="error"
              title="操作失败"
              subTitle={registerStatus.error || '未知错误'}
              extra={[
                <Button key="retry" type="primary" onClick={handleRetry}>
                  重新配置
                </Button>,
                <Button key="close" onClick={handleClose}>
                  关闭
                </Button>,
              ]}
            />
          )}

          {/* Polling indicator */}
          {polling &&
            registerStatus?.status !== FeishuRegisterState.FAILED &&
            registerStatus?.status !== FeishuRegisterState.COMPLETED && (
              <Typography.Text
                type="secondary"
                style={{ display: 'block', marginTop: 16, fontSize: 12 }}
              >
                <Spin size="small" style={{ marginRight: 6 }} />
                自动检测扫码状态中…
              </Typography.Text>
            )}

          {/* Cancel during scanning */}
          {registerStatus?.status !== FeishuRegisterState.FAILED &&
            registerStatus?.status !== FeishuRegisterState.COMPLETED && (
              <div style={{ marginTop: 24 }}>
                <Button onClick={handleClose}>取消</Button>
              </div>
            )}
        </div>
      )}

      {/* Step 3: Result */}
      {step === 'result' && createdAccount && (
        <Result
          status="success"
          title="飞书机器人配置成功"
          subTitle={`账号「${createdAccount.name || createdAccount.id}」已自动上线`}
          extra={[
            <Button key="done" type="primary" onClick={handleSuccessClose}>
              完成
            </Button>,
          ]}
        >
          <div style={{ textAlign: 'left', maxWidth: 360, margin: '0 auto' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              账号 ID：{createdAccount.id}
            </Typography.Text>
            {createdAccount.credential?.appId && (
              <Typography.Text
                type="secondary"
                style={{ display: 'block', fontSize: 12, marginTop: 4 }}
              >
                App ID：{createdAccount.credential.appId}
              </Typography.Text>
            )}
          </div>
        </Result>
      )}

      {step === 'result' && !createdAccount && registerError && (
        <Result
          status="error"
          title="操作失败"
          subTitle={registerError}
          extra={[
            <Button key="retry" type="primary" onClick={handleRetry}>
              重新配置
            </Button>,
            <Button key="close" onClick={handleClose}>
              关闭
            </Button>,
          ]}
        />
      )}
    </Modal>
  );
}
